import { Component, OnInit } from '@angular/core';
import { ModalController, AlertController, NavParams, Platform } from 'ionic-angular';
import { MeteorObservable } from 'meteor-rxjs';
import * as _ from 'lodash';
import * as moment from 'moment';
import { CalendarModal } from 'ion2-calendar';

import { ActivityStatus, Project, User, Update } from 'api/models';
import { Activities, Updates, Users } from 'api/collections';

import { ActivityListPage } from '../activities/activities';
import { NewUpdateComponent } from '../new-update/new-update';
import * as permissions from '../../services/permissions';
// import { getProject } from '../../services/projectData';
import { ProjectService } from '../../services/projectData';
import { HttpService } from '../../services/httpService';
import { AuthenticationService } from '../../services/congito';
import { SharedService } from '../../services/sharedService';

// this is an experiment.  This component only requires certain properties for an Activity,
// so we'd ideally like to strictly define what we expect Activities to have.
// I can't figure out the correct way to tell Typescript to ensure that all properties defined here are in fact subtypes of the Activity Model
// In flow, it'd be something like (ThisComponentActivity & $Shape<CompleteActivity>)
// Note that this does *not* ensure that the .find() will retrieve these properties; to do so we'd need a runtime check
// Still, this seems better than nothing; it at least makes it clear what we need (and therefore what we need to make sure is published)
interface MyActivity {
  earlyStart: Date,
    earlyFinish: Date,
    actualFinish ? : Date,
    assignedUserId: string,
    criticality: string,
    isFlagged: boolean,
    activityStatus: string,
};

type UserPerformanceRow = {
  efficiency: void | number,
  assigned: number,
  completed: number,
  late: number,
  flagged: number,
};
type UserPerformanceMap_UserEntry = {
  all: UserPerformanceRow,
  critical: UserPerformanceRow,
  nonCritical: UserPerformanceRow,
};
@Component({
  selector: 'current-update',
  templateUrl: 'current-update.html'
})
export class CurrentUpdate implements OnInit {
  project ? : Project | void;
  currentUpdate ? : Update | void;
  usersWithActivities ? : Array < User > ;
  lastArchivedUpdate ? : Update | void;
  debouncedCalculatePerformanceMap: any;
  showStatsMap: Object = {};
  updaterPerformanceMap: {
    [key: string]: UserPerformanceMap_UserEntry,
  };

  constructor(
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private navParams: NavParams,
    private platform: Platform,
    public httpService: HttpService,
    public projectService: ProjectService,
    private sharedService: SharedService,
    public authenticationService: AuthenticationService
  ) {
    this.debouncedCalculatePerformanceMap = _.debounce(() => {
      this.calculatePerformanceMap();
    }, 300);
    this.updaterPerformanceMap = {};
  }

  mainSubscription(){
    const projectIdReactive = this.navParams.get('projectIdReactive');
    const projectId = projectIdReactive && projectIdReactive.get();
    this.project = this.projectService.getProject(projectId);
    if(this.project && this.project.currentUpdateId) {
      this.currentUpdate = Updates.findOne(this.project.currentUpdateId);
      this.httpService.MeteorSubscribe('updatePerformance', this.project.currentUpdateId)
      .then(() => {
        this.debouncedCalculatePerformanceMap();
      })
      .catch(err => console.log('meteor subscribe error:', err));

      this.lastArchivedUpdate = Updates.find({
        projectId: this.project._id,
        archived: true,
      }, {
        sort: { completionDate: -1 },
        limit: 1,
      }).fetch()[0];
      const activities = Activities.find({
        updateId: this.project.currentUpdateId,
      }).fetch();
      const memberIds = _.uniq(activities.map(activity => activity.assignedUserId));
      this.usersWithActivities = _.orderBy(Users.find({
        _id: {
          $in: memberIds,
        },
      }).fetch(), [
        user => this.isProjectManager(user),
        'profile.name',
      ], ['desc', 'asc']);
      this.debouncedCalculatePerformanceMap();
    } else {
      this.currentUpdate = undefined;
      this.lastArchivedUpdate = undefined;
      this.debouncedCalculatePerformanceMap();
    }
  }

  ngOnInit(): void {
    MeteorObservable.autorun().subscribe(() => {
      this.mainSubscription()
    });
  }

  presentPlannedEndDateModal(): void {
    const currentUpdateId = this.project && this.project.currentUpdateId;
    if(!currentUpdateId) {
      return;
    }
    const myCalendar = this.modalCtrl.create(CalendarModal, {
      options: {
        title: 'Planned End Date',
        pickMode: 'single',
      }
    });

    myCalendar.onDidDismiss(date => {
      if(!date) {
        return;
      }
      this.httpService.MeteorCall('updateUpdate', currentUpdateId, {
        plannedEndDate: new Date(date.time),
      });
    });

    myCalendar.present();
  }

  canAddNewUpdate(): boolean {
    return this.platform.is('core'); // core means desktop
  }

  presentReminderDateModal(): void {
    const currentUpdate = this.currentUpdate;
    const currentUpdateId = this.project && this.project.currentUpdateId;
    if(!currentUpdateId || !currentUpdate) {
      return;
    }
    const myCalendar = this.modalCtrl.create(CalendarModal, {
      options: {
        title: 'Reminder Date',
        pickMode: 'single',
        from: new Date(),
        to: currentUpdate.plannedEndDate,
      }
    });

    myCalendar.onDidDismiss(date => {
      if(!date) {
        return;
      }
      this.httpService.MeteorCall('updateUpdate', currentUpdateId, {
        reminderDate: new Date(date.time),
      });
    });

    myCalendar.present();
  }

  addUpdate(): void {
    if(!this.project) {
      return;
    }
    const modal = this.modalCtrl.create(NewUpdateComponent, { projectId: this.project._id }, { enableBackdropDismiss: false });
    modal.present();
  }

  // this has a weird signiture because we cant trust activity.earlyStart, and might as well contain the weird logic
  getDaysLate(activity: {
    actualFinish ? : Date,
    earlyStart ? : Date | string,
    earlyFinish ? : Date | string,
  }): { daysLate: number, targetStart: Date, targetFinish: Date } {
    const targetStart = activity.earlyStart instanceof Date ? moment(activity.earlyStart) : undefined;
    const targetFinish = activity.earlyFinish instanceof Date ? moment(activity.earlyFinish) : undefined;
    const actualFinish = moment(activity.actualFinish || Date.now());
    return {
      daysLate: (actualFinish ? actualFinish : moment()).diff(targetFinish, 'days'),
      targetStart: targetStart.toDate(),
      targetFinish: targetFinish.toDate(),
    };
  }

  private makeUserPerformanceRow(activities: Array < MyActivity > ): UserPerformanceRow {
    const lateActivities = activities.filter(activity => {
      const { daysLate } = this.getDaysLate(activity);
      return daysLate > 0;
    });

    const completedActivities = activities.filter(activity => {
      return activity.activityStatus === ActivityStatus.TK_Complete;
    });
    const now = Date.now();
    const activitiesSupposedToBeCompletedByNow = activities.filter(activity => {
      const targetFinish = activity.earlyFinish instanceof Date ? moment(activity.earlyFinish) : undefined;
      return moment(targetFinish).toDate().getTime() <= now;
    });
    const efficiencyPercent = activitiesSupposedToBeCompletedByNow.length === 0 ? undefined : Math.round(completedActivities.length / activitiesSupposedToBeCompletedByNow.length * 100);

    const lateActivitiesTotal = lateActivities.length;

    const numFlagged = activities.filter(activity => activity.isFlagged).length;
    const userPerformanceRow: UserPerformanceRow = {
      efficiency: efficiencyPercent,
      assigned: activities.length,
      completed: completedActivities.length,
      late: lateActivitiesTotal,
      flagged: numFlagged,
    };
    return userPerformanceRow;
  }

  getFilterCodeNameForUser(userId: string): void | string {
    const subCode = this.currentUpdate && this.currentUpdate.subCodesByUser && this.currentUpdate.subCodesByUser[userId];
    return (subCode && subCode.actv_code_name) || undefined;
  }

  getPerfOverviewLevel(user: User): 'verypoor' | 'poor' | 'good' | 'verygood' {
    const entry = this.updaterPerformanceMap[user._id];
    if(!entry) {
      return;
    }

    const criticalPercent = entry.critical && entry.critical.efficiency;
    if(criticalPercent === undefined || criticalPercent === null) {
      // if the user doesnt have critical activities, we instead will show the indicator using the non critical
      const noncriticalPercent = entry.nonCritical && entry.nonCritical.efficiency;
      if(noncriticalPercent === undefined || noncriticalPercent === null) {
        return 'verypoor';
      }

      if(noncriticalPercent >= 75) {
        return 'verygood';
      } else if(noncriticalPercent >= 50) {
        return 'good';
      } else if(noncriticalPercent >= 25) {
        return 'poor';
      }
      return 'verypoor';
    }

    if(criticalPercent >= 100) {
      return 'verygood';
    } else if(criticalPercent >= 90) {
      return 'good';
    } else if(criticalPercent >= 75) {
      return 'poor';
    }
    return 'verypoor';
  }

  getPerfOverviewText(user: User): string {
    const level = this.getPerfOverviewLevel(user);

    if(level === 'verypoor') {
      return 'Very Poor';
    } else if(level === 'poor') {
      return 'Poor';
    } else if(level === 'good') {
      return 'Good';
    } else if(level === 'verygood') {
      return 'Very Good';
    }
  }

  getPerfOverviewClass(user: User): void | string {
    const level = this.getPerfOverviewLevel(user);
    if(!level) {
      return;
    }

    if(level === 'verypoor') {
      return 'verypoor';
    } else if(level === 'poor') {
      return 'poor';
    } else if(level === 'good') {
      return 'good';
    } else if(level === 'verygood') {
      return 'verygood';
    }
  }

  isReviewActivitiesVisible(user: User): boolean {
    const viewer = this.authenticationService.MeteorUser();
    return !(viewer._id === user._id && this.isProjectManager(viewer));
  }

  openActivityListForUser(user: User): void {
    if(!this.project) {
      return;
    }

    const projectIdReactive = new ReactiveVar(this.project._id);
    const requestedUserIdReactive = new ReactiveVar(user._id);
    const modal = this.modalCtrl.create(ActivityListPage, {
      projectIdReactive,
      requestedUserIdReactive,
      useOwnHeader: true,
    }, {
      // enableBackdropDismiss: false,
    });
    modal.present();
  }

  calculatePerformanceMap() {
    if(!this.project) {
      return;
    }


    const allActivities: Array < MyActivity > = Activities.find({
      updateId: this.project.currentUpdateId,
      assignedUserId: {
        $in: (this.usersWithActivities || []).map(user => user._id),
      },
    }).fetch() as Array < MyActivity > ;
    const activitiesByAssignedUser = _.groupBy(allActivities, activity => activity.assignedUserId);


    this.updaterPerformanceMap = _.mapValues(_.keyBy(this.usersWithActivities, user => user._id), user => {
      const usersActivities = activitiesByAssignedUser[user._id] || [];

      const [criticalActivities, nonCriticalActivities] = _.partition(usersActivities, (activity: MyActivity): boolean => activity.criticality === 'Y');
      return {
        all: this.makeUserPerformanceRow(usersActivities),
        critical: this.makeUserPerformanceRow(criticalActivities),
        nonCritical: this.makeUserPerformanceRow(nonCriticalActivities),
      };
    });
  }

  terminateUpdatePeriod(): void {
    const projectId = this.project && this.project._id;
    if(!projectId) {
      console.warn('Missing project');
      return;
    }

    const updateId = this.currentUpdate && this.currentUpdate._id;
    if(!updateId) {
      console.warn('Missing currentUpdate');
      return;
    }

    const alert = this.alertCtrl.create({
      title: 'Confirm',
      message: 'Are you sure you would like to terminate this update period?',
      buttons: [{
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Terminate',
          handler: () => {
            /*
            MeteorObservable.call('terminateUpdatePeriod', { projectId, updateId }).subscribe({
              error: (e: Error) => {
                if(e) {
                  // this.handleError(e);
                }
              }
            });
            */
            this.httpService.MeteorCall(
              'terminateUpdatePeriod',
              { projectId, updateId }
            ).then(() => {})
            .catch((e: Error) => {
              if (e) {
                // this.handleError(e);
              }
            });
            alert.dismiss();
            return false;
          }
        }
      ]
    });

    alert.present();
  }

  completeUpdatePeriod(): void {
    const projectId = this.project && this.project._id;
    if(!projectId) {
      console.warn('Missing project');
      return;
    }

    const updateId = this.currentUpdate && this.currentUpdate._id;
    if(!updateId) {
      console.warn('Missing currentUpdate');
      return;
    }

    const alert = this.alertCtrl.create({
      title: 'Confirm',
      message: 'Are you sure you would like to complete this update period?',
      buttons: [{
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Complete',
          handler: () => {
            this.httpService.MeteorCall('completeUpdatePeriod', projectId, updateId)
            .catch((err: Error) => {
              if(err) {
                // this.handleError(e);
                console.error('CurrentUpdate error: ', err);
              }
            });
            alert.dismiss();
            return false;
          }
        }
      ]
    });

    alert.present();
  }

  toggleStats(user) {
    this.showStatsMap[user._id] = !this.showStatsMap[user._id];
  }

  isProjectManager(user) {
    return this.project ? permissions.isProjectManager(this.project, user) : false;
  }
}



// WEBPACK FOOTER //
// ./src/pages/current-update/current-update.ts